import React, { useState } from 'react';
import { Property } from '../../types';
import { Heart, MapPin, Bed, Bath, Square, Eye, Share2 } from 'lucide-react';
import { useProperty } from '../../context/PropertyContext';
import { useAuth } from '../../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../utils/formatters';
import { useLanguage } from '../../context/LanguageContext';

interface PropertyCardProps {
  property: Property;
  onClick: () => void;
  variant?: 'default' | 'featured' | 'compact';
}

const PropertyCard: React.FC<PropertyCardProps> = ({ 
  property, 
  onClick, 
  variant = 'default' 
}) => {
  const { toggleFavorite, favorites } = useProperty();
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const isFavorite = favorites.includes(property.id);
  
  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      const redirectTo = `${location.pathname}${location.search}${location.hash}`;
      navigate(`/login?redirect=${encodeURIComponent(redirectTo)}`);
      return;
    }
    await toggleFavorite(property.id);
  };

  const handleShareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      navigator.share({
        title: property.title,
        text: `Check out this property: ${property.title}`,
        url: window.location.origin + `/property/${property.id}`
      });
    }
  };

  // Hide exact address, show only city and state
  const locationDisplay = `${property.address.city}, ${property.address.state}`;

  const cardClasses = variant === 'featured' 
    ? 'card hover-lift hover-glow lg:col-span-2 lg:row-span-2' 
    : variant === 'compact'
    ? 'card hover-lift hover-glow max-w-sm'
    : 'card hover-lift hover-glow';

  const imageClasses = variant === 'featured' 
    ? 'w-full h-64 lg:h-80 object-cover' 
    : variant === 'compact'
    ? 'w-full h-40 object-cover'
    : 'w-full h-48 object-cover';

  return (
    <div 
      className={`${cardClasses} cursor-pointer group relative overflow-hidden`}
      onClick={onClick}
    >
      <div className="relative">
        {/* Image with loading state */}
        <div className="relative overflow-hidden rounded-t-2xl">
          {!imageLoaded && !imageError && (
            <div className={`${imageClasses.replace('object-cover', '')} bg-neutral-200 animate-pulse flex items-center justify-center`}>
              <div className="w-12 h-12 text-neutral-400">
                <Eye className="w-full h-full" />
              </div>
            </div>
          )}
          
          {!imageError && (
            <img 
              src={property.images[0]} 
              alt={property.title}
              className={`${imageClasses} transition-transform duration-500 group-hover:scale-105 ${!imageLoaded ? 'hidden' : ''}`}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          )}
          
          {imageError && (
            <div className={`${imageClasses.replace('object-cover', '')} bg-neutral-100 flex items-center justify-center`}>
              <div className="text-neutral-400 text-center">
                <Eye className="w-12 h-12 mx-auto mb-2" />
                <p className="text-sm">{t('property.imageUnavailable')}</p>
              </div>
            </div>
          )}

          {/* Gradient overlay */}
          <div className="gradient-overlay opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          {/* Action buttons */}
          <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
            <button 
              onClick={handleFavoriteClick}
              className={`p-2.5 rounded-full transition-all duration-300 backdrop-blur-md ${
                isFavorite 
                  ? 'bg-red-500 text-white shadow-large' 
                  : 'bg-white/90 text-neutral-600 hover:bg-white shadow-medium'
              }`}
              aria-label={isFavorite ? t('property.removeFromFavorites') : t('property.addToFavorites')}
            >
              <Heart size={16} className={isFavorite ? 'fill-current' : ''} />
            </button>
            
            {typeof navigator !== 'undefined' && 'share' in navigator && (
              <button 
                onClick={handleShareClick}
                className="p-2.5 rounded-full bg-white/90 text-neutral-600 hover:bg-white shadow-medium backdrop-blur-md transition-all duration-300"
                aria-label={t('property.shareProperty')}
              >
                <Share2 size={16} />
              </button>
            )}
          </div>

          {/* Status badge */}
          <div className="absolute bottom-3 left-3">
            <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium backdrop-blur-md ${
              property.status === 'sale' 
                ? 'bg-primary-600/90 text-white' 
                : property.status === 'rent'
                ? 'bg-accent-600/90 text-white'
                : 'bg-secondary-600/90 text-white'
            }`}>
              {property.status === 'sale' ? t('property.forSale') : t('property.forRent')}
            </span>
          </div>
        </div>
      </div>

      <div className={`p-4 ${variant === 'featured' ? 'lg:p-6' : ''}`}>
        {/* Title and Location */}
        <div className="mb-3">
          <h3 className={`font-semibold text-neutral-800 line-clamp-2 mb-1 ${
            variant === 'featured' ? 'text-xl lg:text-2xl' : 'text-lg'
          }`}>
            {property.title}
          </h3>
          <div className="flex items-center text-neutral-500 text-sm">
            <MapPin size={14} className="mr-1 flex-shrink-0" />
            <span className="line-clamp-1">{locationDisplay}</span>
          </div>
        </div>

        {/* Price */}
        <div className="mb-4">
          <p className={`font-bold text-primary-600 ${
            variant === 'featured' ? 'text-2xl lg:text-3xl' : 'text-xl'
          }`}>
            {property.status === 'rent' 
              ? `${formatCurrency(property.price)}/${t('property.perMonth')}` 
              : formatCurrency(property.price)}
          </p>
        </div>

        {/* Features */}
        <div className={`flex items-center justify-between text-neutral-600 ${
          variant === 'featured' ? 'text-base' : 'text-sm'
        }`}>
          <div className="flex items-center gap-1">
            <Bed size={16} className="text-neutral-400" />
            <span>{property.features.bedrooms}</span>
            <span className="text-neutral-400">
              {property.features.bedrooms === 1 ? t('property.bed') : t('property.beds')}
            </span>
          </div>
          
          <div className="flex items-center gap-1">
            <Bath size={16} className="text-neutral-400" />
            <span>{property.features.bathrooms}</span>
            <span className="text-neutral-400">
              {property.features.bathrooms === 1 ? t('property.bath') : t('property.baths')}
            </span>
          </div>
          
          <div className="flex items-center gap-1">
            <Square size={16} className="text-neutral-400" />
            <span>{property.features.area.toLocaleString()}</span>
            <span className="text-neutral-400">{t('property.sqft')}</span>
          </div>
        </div>

        {/* Additional info for featured variant */}
        {variant === 'featured' && (
          <div className="mt-4 pt-4 border-t border-neutral-200">
            <p className="text-neutral-600 text-sm line-clamp-2">
              {property.description || "Beautiful property in a great location with modern amenities and stunning views."}
            </p>
          </div>
        )}
      </div>

      {/* Hover effect overlay */}
      <div className="absolute inset-0 border-2 border-primary-500 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
    </div>
  );
};

export default PropertyCard;
